import { Injectable } from '@angular/core';
import { RiskTemplate } from '../models/risk-template';
import { Threat } from '../models/threat';
import { Observable, of } from 'rxjs';
import { ThreatType } from '../models/threat-type';
import { BaseResponse } from '../models/base-response';
import { EntityType } from '../models/entity-type';
import { AssessmentTarget } from '../models/assessment-target';
import { RiskFormula, RiskFormulaTestResult } from '../models/risk-formula';
import { RiskFormulaType } from '../models/risk-formula-type';
import { AssessmentDistributionRequest } from '../models/assessment-distribution-request';
import { AssessmentSummary } from '../models/assessment-summary';
import { Assessment } from '../models/assessment';
import { RiskTemplateFilter } from '../models/risk-template-filter';
import { PageResponse } from '../models/page-response';
import { NameEntityTypeFilter } from '../models/name-entity-type-filter';
import { PageRequest } from '../models/page-request';
import { FilterType } from '../models/filter-type';
import { AssessmentDistributionRisk } from '../models/assessment-distribution-risk';
import { AssessmentMemberData } from '../models/assessment-member-data';
import { AssessmentMemberAnswer } from '../models/assessment-member-answer';
import { mockedData } from '../assets/mock-data/mocked-agility-risk-service-data';
import { DistributionRequest } from '../models/distribution-request';
import { RiskFormulaFilter } from '../models/risk-formula-filter';
import { RiskFactor } from '../models/risk-factor';
import { RiskBand } from '../models/risk-band';
import { AssessmentDistribution } from '../models/assessment-distribution';
import { AssessmentDistributionInfo } from '../models/assessment-distribution-info';
import { AssessmentDistributionSurvey } from '../models/assessment-distribution-survey';
import { CustomField } from '../models/custom-field.interface';
import { AssessmentComment } from '../models/assessment-comment';
import { Question } from '../models/question';
import { RiskDetail } from '../models/risk-detail';
import { TemplateAccess } from '../models/template-access';
import { GetRiskAssessmentSummaryResponse } from '../models/get-risk-assessment-summary-response';

export abstract class AgilityRiskService {
  abstract getRiskTemplateHeader(templateId: string): Observable<RiskTemplate>;

  abstract saveRiskTemplateHeader(template: RiskTemplate): Observable<RiskTemplate>;

  abstract copyRiskTemplate(templateId: string): Observable<BaseResponse>;

  abstract createAssessmentAndSendDistribution(request: AssessmentDistributionRequest): Observable<Assessment>;

  abstract createRiskBand(formulaId: string, riskBand: any): Observable<RiskBand>;

  abstract deleteRiskBand(riskBandId: string): Observable<BaseResponse>;

  abstract deleteRiskFactor(factorId: string): Observable<any>;

  abstract deleteRiskTemplate(templateId: string): Observable<BaseResponse>;

  abstract deleteThreatType(threatType: ThreatType): Observable<BaseResponse>;

  abstract editRiskBand(riskBand: RiskBand): Observable<BaseResponse>;

  abstract getAssessment(assessmentId: string): Observable<Assessment>;

  abstract getAssessmentDistribution(distributionId?: string): Observable<AssessmentDistribution | AssessmentDistributionInfo>;

  abstract getAssessmentRisks(assessmentId: string): Observable<Array<AssessmentDistributionRisk>>;

  abstract getRiskAssessmentSummary(assessmentId: string): Observable<GetRiskAssessmentSummaryResponse>;

  abstract getAssessmentTargets(pageRequest: PageRequest<NameEntityTypeFilter>): Observable<PageResponse<AssessmentTarget>>;

  abstract getAssessments(pageRequest: PageRequest<NameEntityTypeFilter>): Observable<PageResponse<Assessment>>;

  abstract getCustomFields(area: any): Observable<Array<CustomField>>;

  abstract saveCustomField(customField: CustomField): Observable<any>;

  abstract deleteCustomField(customFieldId: string): Observable<any>;

  abstract reorderCustomFields(area: string, data: any): Observable<any>;

  abstract getSurveyUserId(): any;

  abstract getRiskFactors(): Observable<Array<RiskFactor>>;

  abstract getRiskFormula(formulaId: string): Observable<RiskFormula>;

  abstract saveRiskFormula(formula: RiskFormula): Observable<any>;

  abstract deleteRiskFormula(formulaId: string): Observable<BaseResponse>;

  abstract testRiskFormula(formula: RiskFormula, riskFactors: Array<RiskFactor>): Observable<RiskFormulaTestResult>;

  abstract getRiskFormulaBands(formulaId: string): Observable<Array<RiskBand>>;

  abstract getRiskFormulaFactors(formulaId: string): Observable<Array<RiskFactor>>;

  abstract deleteRiskFormulaFactor(factorId: string): Observable<BaseResponse>;

  abstract getRiskFormulas(pageRequest: PageRequest<RiskFormulaFilter>): Observable<PageResponse<RiskFormula>>;

  abstract getRiskTemplateTargetEntityTypes(): Observable<Array<EntityType>>;

  abstract getRiskTemplates(riskTemplateFilter?: RiskTemplateFilter, page?: number, size?: number): Observable<PageResponse<RiskTemplate>>;

  abstract getThreatTypes(): Observable<Array<ThreatType>>;

  abstract getThreats(riskTemplateId: string, optionalParams?: any): Observable<Array<Threat>>;

  abstract getThreat(threatId: string): Observable<Threat>;

  abstract deleteThreat(threatId: string): Observable<BaseResponse>;

  abstract postAssessmentDistributionRisk(distributionId: string, assessmentDistributionRisks: AssessmentDistributionRisk[], comment: string): Observable<BaseResponse>;

  abstract putRiskBandsPosition(orderedRiskBands: Array<RiskBand>): Observable<Array<RiskBand>>;

  abstract putThreatTypesPosition(orderedThreatTypes: Array<ThreatType>): Observable<Array<ThreatType>>;

  abstract saveRiskFactor(formulaId: string, factor: RiskFactor): Observable<any>;

  abstract saveThreatType(threatType: ThreatType): Observable<ThreatType>;

  abstract sendAssessmentDistribution(distribution: DistributionRequest): Observable<BaseResponse>;

  abstract getAssessmentDistributionByMember(assessmentDistributionMemberId: string): Observable<AssessmentDistributionSurvey>;

  abstract getThreatQuestions(threatId: string): Observable<Array<Question>>;

  abstract saveThreatQuestions(threatId: string, questions: Array<Question>): Observable<Array<Question>>;

  abstract getAssessmentSummary(assessmentId: string): Observable<AssessmentSummary>;

  abstract getRiskAssessments(request?: PageRequest<NameEntityTypeFilter>): Observable<Array<Assessment>>;

  abstract createThreat(riskTemplateId: string, threat: any): Observable<Threat>;

  abstract updateThreat(threat: any): Observable<Threat>;

  abstract getRiskAssessmentComments(id: string): Observable<Array<AssessmentComment>>;

  abstract getRiskAssessmentRisksSummary(id: string): Observable<RiskDetail>;

  abstract putRiskAssessmentComments(id: any, comments: AssessmentComment[]): Observable<BaseResponse>;

  abstract getRiskAssessmentRiskAnswers(id: any): Observable<AssessmentMemberData[]>;

  abstract saveTemplateThreats(templateId: string, threatIds: string[]): Observable<BaseResponse>;

  abstract getManagerAccessList(size?: number, page?: number, sort?: string): Observable<Array<TemplateAccess>>;

  abstract getManagerAccess(id: string): Observable<TemplateAccess>;

  abstract saveManagerAccess(templateAccess: TemplateAccess): Observable<TemplateAccess>;

  abstract deleteManagerAccess(id: string): Observable<BaseResponse>;

  abstract putRiskAssessmentAnswer(assessmentMemberAnswer: AssessmentMemberAnswer): Observable<BaseResponse>;

  abstract getRiskAssessmentRiskCustomValues(id: any): Observable<AssessmentMemberData[]>;

  abstract putRiskAssessmentStatus(id: any, status: string): Observable<BaseResponse>;

  abstract putRiskAssessmentRiskCustomValues(id: any, assessmentMemberData: AssessmentMemberData[]): Observable<BaseResponse>;

  abstract getRiskBandRecalculation(formula: RiskFormula): Observable<Array<RiskBand>>;

  abstract putRiskAssessment(assessmentSummary: AssessmentSummary): Observable<BaseResponse>;

  abstract postRiskAssessmentEmail(id: any, sendAll?: boolean, contactId?: any): Observable<BaseResponse>;

  abstract deleteRiskAssessment(id: any): Observable<BaseResponse>;

  abstract getLoggedInUser(): { id: number | string; role: string };
}

/**
 * This service is just a placeholder that does nothing, in case you don't need a service at all
 */
@Injectable()
export class RiskDummyService implements AgilityRiskService {

  getRiskTemplates(riskTemplateFilter?: RiskTemplateFilter, page?: number, size?: number): Observable<PageResponse<RiskTemplate>> {
    const response = new PageResponse<RiskTemplate>();
    response.content = mockedData.riskTemplates.filter((riskTemplate: RiskTemplate) =>
      !riskTemplateFilter || !riskTemplateFilter.entityTypeArea || riskTemplate.targetEntityType === riskTemplateFilter.entityTypeArea)
      .map((riskTemplate: RiskTemplate) => {
        // Backend service will most likely omit children entities at this level
        // riskTemplate.threats = null;
        return riskTemplate;
      });
    return of(response);
  }

  getThreats(riskTemplateId: string, optionalParams?: any): Observable<Array<Threat>> {
    return of(mockedData.riskTemplates.find((riskTemplate: RiskTemplate) =>
      riskTemplate.id === riskTemplateId)?.threats as Array<Threat>);
  }


  getThreat(threatId: string): Observable<Threat> {
    return of(mockedData.threat);
  }

  getThreatTypes(): Observable<Array<ThreatType>> {
    return of(mockedData.threatTypes
      .sort((a: ThreatType, b: ThreatType) =>
        a.numericValue < b.numericValue ? -1 : a.numericValue > b.numericValue ? 1 : 0)
      .map((threatType: ThreatType) => Object.assign({}, threatType)));
  }

  saveThreatType(threatType: ThreatType): Observable<ThreatType> {
    if (!threatType.id) {
      // Post new threat type
      threatType.id = (mockedData.threatTypes
        .reduce((acc: number, val: ThreatType) => Math.max(parseInt(val.id, 10), acc), 0) + 1).toString(10);
      threatType.numericValue = threatType.numericValue ||
        mockedData.threatTypes.reduce((acc: number, val: ThreatType) => Math.max(val.numericValue, acc), 0) + 1;
      mockedData.threatTypes.push(threatType);
      mockedData.threatTypes.sort((a: ThreatType, b: ThreatType) =>
        a.numericValue < b.numericValue ? -1 : a.numericValue > b.numericValue ? 1 : 0);
      return of(threatType);
    } else {
      const index = mockedData.threatTypes.findIndex((persistedThreatType: ThreatType) => persistedThreatType.id === threatType.id);
      mockedData.threatTypes.splice(index, 1);
      mockedData.threatTypes.push(threatType);
      mockedData.threatTypes.sort((a: ThreatType, b: ThreatType) =>
        a.numericValue < b.numericValue ? -1 : a.numericValue > b.numericValue ? 1 : 0);
      return of(threatType);
    }
  }

  putThreatTypesPosition(orderedThreatTypes: Array<ThreatType>): Observable<Array<ThreatType>> {
    let newSequenceNumber = 0;
    orderedThreatTypes.forEach((threatType: ThreatType) => {
      const tobeOrderedThreatType = mockedData.threatTypes.find((existingThreatType: ThreatType) => existingThreatType.id === threatType.id);
      tobeOrderedThreatType.numericValue = newSequenceNumber;
      newSequenceNumber++;
    });
    mockedData.threatTypes.sort((a: ThreatType, b: ThreatType) =>
      a.numericValue < b.numericValue ? -1 : a.numericValue > b.numericValue ? 1 : 0);
    return of(mockedData.threatTypes);
  }


  deleteThreatType(threatType: ThreatType): Observable<BaseResponse> {
    const index = mockedData.threatTypes.findIndex((existingThreatType: ThreatType) => existingThreatType.id === threatType.id);
    mockedData.threatTypes.splice(index, 1);
    let newSequenceNumber = 0;
    mockedData.threatTypes.map((existingThreatType: ThreatType) => {
      existingThreatType.numericValue = newSequenceNumber;
      newSequenceNumber++;
    });
    return of({statusCode: 200} as BaseResponse);
  }

  getRiskTemplateTargetEntityTypes(): Observable<Array<EntityType>> {
    return of([EntityType.department, EntityType.productAndService, EntityType.resource, EntityType.site, EntityType.vendor]);
  }

  getAssessmentTargets(request: PageRequest<NameEntityTypeFilter>): Observable<PageResponse<AssessmentTarget>> {
    const pageResponse = new PageResponse<AssessmentTarget>();
    pageResponse.number = request.page;
    pageResponse.size = request.size;
    pageResponse.numberOfElements = mockedData.assessments.length;
    const filteredTargets = mockedData.assessmentTargets.filter((surveyTarget: AssessmentTarget) => request.query.entityTypeArea === surveyTarget.entityType).map((surveyTarget: AssessmentTarget) => Object.assign({}, surveyTarget));
    const start = pageResponse.size * pageResponse.number;
    if (filteredTargets.length > start) {
      let end: number;
      if (filteredTargets.length > start + request.size) {
        end = start + request.size;
      } else {
        end = filteredTargets.length;
      }
      pageResponse.content = filteredTargets.slice(start, end);
    } else {
      pageResponse.content = [];
    }

    return of(pageResponse);
  }

  getRiskFormulas(pageRequest: PageRequest<RiskFormulaFilter>): Observable<PageResponse<RiskFormula>> {
    const pageResponse = new PageResponse<RiskFormula>();
    pageResponse.number = pageRequest.page;
    pageResponse.size = pageRequest.size;
    pageResponse.numberOfElements = mockedData.assessments.length;
    let filteredFormulas = mockedData.riskFormulas.filter((riskFormula: RiskFormula) => riskFormula.formulaType === RiskFormulaType.multiPart || riskFormula.formulaType === RiskFormulaType.simple).map(
      (riskFormula: RiskFormula) => {
        if (riskFormula.formulas && riskFormula.formulas.length) {
          riskFormula.formulas = riskFormula.formulas.map((rf: RiskFormula) => {
            rf.factors = []; // These won't be returned in the paginated list view
            return Object.assign({}, rf);
          });
        }
        return riskFormula;
      }
    );

    if (pageRequest.query?.name && pageRequest.query.name.filterType === FilterType.contains) {
      filteredFormulas = filteredFormulas.filter((formula: RiskFormula) => formula.name.indexOf(pageRequest.query.name.stringValue) >= 0);
    } else if (pageRequest.query?.name && pageRequest.query.name.filterType === FilterType.startsWith) {
      filteredFormulas = filteredFormulas.filter((formula: RiskFormula) => formula.name.startsWith(pageRequest.query.name.stringValue));
    }

    if (pageRequest.query?.entityType) {
      filteredFormulas = filteredFormulas.filter((formula: RiskFormula) => formula.entityType === pageRequest.query.entityType);
    }

    if (pageRequest.query?.status) {
      filteredFormulas = filteredFormulas.filter((formula: RiskFormula) => formula.status === pageRequest.query.status);
    }

    const start = pageResponse.size * pageResponse.number;
    if (filteredFormulas.length > start) {
      let end: number;
      if (filteredFormulas.length > start + pageRequest.size) {
        end = start + pageRequest.size;
      } else {
        end = filteredFormulas.length;
      }
      pageResponse.content = filteredFormulas.slice(start, end);
    } else {
      pageResponse.content = [];
    }

    return of(pageResponse);
  }

  getRiskFormula(formulaId: string): Observable<RiskFormula> {
    return of(Object.assign({}, mockedData.riskFormulas.find((riskFormula: RiskFormula) => riskFormula.id === formulaId)) as RiskFormula);
  }

  saveRiskFormula(formula: RiskFormula): Observable<any> {
    return of(null);
  }

  deleteRiskFormula(formulaId: string): Observable<BaseResponse> {
    return of({statusCode: 200});
  }

  testRiskFormula(formula: RiskFormula, riskFactors: Array<RiskFactor>): Observable<RiskFormulaTestResult> {
    return of({
      impactValue: 2,
      probabilityValue: 3,
      result: 8
    });
  }

  getRiskFormulaFactors(formulaId: string): Observable<Array<RiskFactor>> {
    return of(Object.assign({}, new Array<RiskFactor>()));
  }

  deleteRiskFormulaFactor(factorId: string): Observable<BaseResponse> {
    return of(null);
  }

  getRiskFormulaBands(formulaId: string): Observable<Array<RiskBand>> {
    return of(Object.assign({}, mockedData.riskFormulas.find((riskFormula: RiskFormula) => riskFormula.id === formulaId).bands));
  }

  sendAssessmentDistribution(distribution: DistributionRequest): Observable<BaseResponse> {
    return of({statusCode: 200});
  }

  createAssessmentAndSendDistribution(request: AssessmentDistributionRequest): Observable<Assessment> {
    return of(new Assessment());
  }

  getRiskAssessmentSummary(assessmentId: string): Observable<GetRiskAssessmentSummaryResponse> {
    const surveySummary = new AssessmentSummary();
    const survey = mockedData.assessments.find((survey: Assessment) => survey.id === assessmentId);
    const risks = mockedData.surveyDistributionRisks.filter((risk: AssessmentDistributionRisk) => risk.assessmentId === assessmentId);
    let completedRiskCount = 0;
    let incompleteRiskCount = 0;
    let estimatedTimeInMinutes = 0;
    risks.forEach((risk: AssessmentDistributionRisk) => {
      const answers = risk.riskAssessmentMembersData.filter((surveyMemberData: AssessmentMemberData) => surveyMemberData.memberData.member.id === mockedData.currentLoggedInUser.id)[0].memberAnswers;
      if (answers.filter((answer: AssessmentMemberAnswer) => !(answer.questionAnswer)).length === 0) {
        completedRiskCount++;
      } else {
        incompleteRiskCount++;
        estimatedTimeInMinutes = estimatedTimeInMinutes + risk.estimatedCompletionMinutes;
      }
    });
    surveySummary.id = survey.id;
    surveySummary.completedRiskCount = completedRiskCount;
    surveySummary.totalRiskCount = completedRiskCount + incompleteRiskCount;
    surveySummary.dueDate = survey.dueDate;
    surveySummary.estimatedTimeMinutes = estimatedTimeInMinutes;
    surveySummary.status = survey.status;
    surveySummary.surveyTarget = survey.parent;
    return of(null);
  }

  deleteRiskTemplate(templateId: string): Observable<BaseResponse> {
    return of({statusCode: 200});
  }

  getRiskTemplateHeader(templateId: string): Observable<RiskTemplate> {
    return of(null);
  }

  saveRiskTemplateHeader(template: RiskTemplate): Observable<RiskTemplate> {
    return of(null);
  }

  copyRiskTemplate(templateId: string): Observable<BaseResponse> {
    return of({statusCode: 200});
  }

  getAssessments(pageRequest: PageRequest<NameEntityTypeFilter>): Observable<PageResponse<Assessment>> {
    const pageResponse = new PageResponse<Assessment>();
    pageResponse.number = pageRequest.page;
    pageResponse.size = pageRequest.size;
    pageResponse.numberOfElements = mockedData.assessments.length;
    let filteredSurveys = mockedData.assessments.map((survey: Assessment) => Object.assign({}, survey));
    if (pageRequest.query?.name && pageRequest.query.name.filterType === FilterType.contains) {
      filteredSurveys = filteredSurveys.filter((survey: Assessment) => survey.name.indexOf(pageRequest.query.name.stringValue) >= 0);
    } else if (pageRequest.query?.name && pageRequest.query.name.filterType === FilterType.startsWith) {
      filteredSurveys = filteredSurveys.filter((survey: Assessment) => survey.name.startsWith(pageRequest.query.name.stringValue));
    }

    if (pageRequest.query?.entityTypeArea) {
      filteredSurveys = filteredSurveys.filter((survey: Assessment) => survey.entityType === pageRequest.query.entityTypeArea);
    }

    const start = pageResponse.size * pageResponse.number;
    if (filteredSurveys.length > start) {
      let end: number;
      if (filteredSurveys.length > start + pageRequest.size) {
        end = start + pageRequest.size;
      } else {
        end = filteredSurveys.length;
      }
      pageResponse.content = filteredSurveys.slice(start, end);
    } else {
      pageResponse.content = [];
    }

    return of(pageResponse);
  }

  getAssessment(assessmentId: string): Observable<Assessment> {
    return of(Object.assign({}, mockedData.assessments.find((survey: Assessment) => survey.id === assessmentId)));
  }

  getAssessmentRisks(assessmentId: string): Observable<Array<AssessmentDistributionRisk>> {
    return of(mockedData.surveyDistributionRisks.filter((risk: AssessmentDistributionRisk) => risk.assessmentId === assessmentId).map((risk: AssessmentDistributionRisk) => Object.assign({}, risk)));
  }

  getAssessmentDistribution(distributionId?: string): Observable<AssessmentDistribution | AssessmentDistributionInfo> {
    return of(null);
  }

  getAssessmentDistributionByMember(assessmentDistributionMemberId: string): Observable<AssessmentDistributionSurvey> {
    return of(null);
  }

  createRiskBand(formulaId: string, riskBand: any): Observable<RiskBand> {
    return of(new RiskBand());
  }

  deleteRiskBand(riskBand: string): Observable<BaseResponse> {
    return of({statusCode: 200});
  }

  putRiskBandsPosition(orderedRiskBands: Array<RiskBand>): Observable<Array<RiskBand>> {
    return of(Array<RiskBand>());
  }

  editRiskBand(riskBand: RiskBand): Observable<BaseResponse> {
    return of({statusCode: 200});
  }

  getRiskFactors(): Observable<Array<RiskFactor>> {
    return of(mockedData.riskFactors);
  }

  saveRiskFactor(formulaId: string, factor: RiskFactor): Observable<any> {
    return of(null);
  }

  deleteRiskFactor(factorId: string): Observable<any> {
    return of(null);
  }

  getSurveyUserId(): any {
    return;
  }

  postAssessmentDistributionRisk(): Observable<BaseResponse> {
    return of(null);
  }

  getCustomFields(area: any): Observable<Array<CustomField>> {
    return of(null);
  }

  saveCustomField(customField: CustomField): Observable<any> {
    return of(null);
  }

  deleteCustomField(customFieldId: string): Observable<any> {
    return of(null);
  }

  reorderCustomFields(area: string, data: any): Observable<any> {
    return of(null);
  }

  getThreatQuestions(threatId: string): Observable<Array<Question>> {
    return of(mockedData.threatQuestions);
  }

  saveThreatQuestions(threatId: string, questions: Array<Question>): Observable<Array<Question>> {
    return of(mockedData.threatQuestions);
  }

  getAssessmentSummary(assessmentId: string): Observable<AssessmentSummary> {
    return of(null);
  }

  deleteThreat(threatId: string): Observable<BaseResponse> {
    return of({statusCode: 200});
  }

  getRiskAssessments(): Observable<Array<Assessment>> {
    return of(null);
  }

  getRiskAssessmentComments(id: string): Observable<Array<AssessmentComment>> {
    return of(null);
  }

  getRiskAssessmentRisksSummary(id: string): Observable<RiskDetail> {
    return of(null);
  }

  putRiskAssessmentComments(id: any, comments: AssessmentComment[]): Observable<BaseResponse> {
    return of(null);
  }

  getRiskAssessmentRiskAnswers(id: any): Observable<AssessmentMemberData[]> {
    return of(null);
  }

  createThreat(riskTemplateId: string, threat: any): Observable<Threat> {
    return of(new Threat());
  }

  updateThreat(threat: any): Observable<Threat> {
    return of(new Threat());
  }

  saveTemplateThreats(templateId: string, threatIds: string[]): Observable<BaseResponse> {
    return of(null);
  }

  getManagerAccessList(size?: number, page?: number, sort?: string): Observable<Array<TemplateAccess>> {
    return of(null);
  }

  getManagerAccess(id: string): Observable<TemplateAccess> {
    return of(null);
  }

  saveManagerAccess(templateAccess: TemplateAccess): Observable<TemplateAccess> {
    return of(null);
  }

  deleteManagerAccess(id: string): Observable<BaseResponse> {
    return of();
  }

  putRiskAssessmentAnswer(assessmentMemberAnswer: AssessmentMemberAnswer): Observable<BaseResponse> {
    return of(null);
  }

  getRiskAssessmentRiskCustomValues(id: any): Observable<Array<AssessmentMemberData>> {
    return of(null);
  }

  putRiskAssessmentStatus(id: any, status: string): Observable<BaseResponse> {
    return of(null);
  }

  putRiskAssessmentRiskCustomValues(id: any, assessmentMemberData: AssessmentMemberData[]): Observable<BaseResponse> {
    return of(null);
  };

  getRiskBandRecalculation(formula: RiskFormula): Observable<RiskBand[]> {
    return of(null);
  }

  putRiskAssessment(assessmentSummary: AssessmentSummary): Observable<BaseResponse> {
    return of(null);
  }

  postRiskAssessmentEmail(id: any): Observable<BaseResponse> {
    return of(null);
  }

  deleteRiskAssessment(id: any): Observable<BaseResponse> {
    return of(null);
  }

  getLoggedInUser(): { id: number | string; role: string } {
    return mockedData.currentLoggedInUser as { id: number | string; role: string };
  }
}
